import React, { useState, useRef, useEffect } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { CircularProgress } from "@mui/material";

import { useAuth } from "../../../context/AuthProvider";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import uRLs from "../../../utils/baseUtils";
import { useContextApi } from "../../../context/ContextApi";
import { toPrimaryQuantity, toSecondaryQuantity } from "@heartinz/finance";
import { formatCurrencyFun, formatCurrencyFunWithoutConvert, getDisplayPrices } from "../../../utils/formatCurrencyFunc";
import { CheckCircleIcon, ChevronRightIcon, CloseIcon, XIcon } from "../../../constants/Icons";

const ViewStockArrival = ({ data: initialData, onClose, onAccept }) => {
  // console.log("Initial Data:", initialData);
  const [transferData, setTransferData] = useState(initialData);
  const [isBtnLoading, setBtnLoading] = useState(null);
  const [errorIndex, setErrorIndex] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [mainModalOpen, setMainModalOpen] = useState(true);
  const [landingcost, setLandingCost] = useState("");
  const [landingCostError, setLandingCostError] = useState(null);
  const [isUpdateBtnLoading, setUpdateBtnLoading] = useState(false);
  const { useEscapeKey } = useContextApi();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const LandingCostRef = useRef(null);
  const { isUser } = useAuth();

  const [selectedUnits, setSelectedUnits] = useState({});

  useEffect(() => {
    if (viewModalOpen && LandingCostRef.current) {
      LandingCostRef.current.focus();
    }
  }, [viewModalOpen]);

  const handleInputFocus = (index) => {
    setErrorIndex(null);
  };

  const handleAccept = async (transferid, item, index) => {
    // console.log("Accept clicked for item:", item);
    const quantity = quantities[index];
    if (!quantity || quantity <= 0) {
      setErrorIndex(index);
      return;
    }
    const enteredQty = Number(quantity);

    const itemData = transferData.transferitems[index];

    const primaryUnit = itemData?.unitConfig?.primaryUnitCode;

    const secondaryUnit = itemData?.unitConfig?.secondaryUnitCode;

    const selectedUnit =
      selectedUnits[index] ||
      (itemData?.isSecondaryUnit ? secondaryUnit : primaryUnit);

    const hasUnitConfig =
      itemData?.unitConfig &&
      itemData?.unitConfig?.secondaryUnitCode &&
      itemData?.unitConfig?.conversion;

    const isSecondary = hasUnitConfig && selectedUnit === secondaryUnit;
    // convert only when secondary selected
    const finalQty =
      itemData?.unitConfig && isSecondary
        ? (() => {
            const converted = toPrimaryQuantity(
              enteredQty,
              itemData.unitConfig,
            );

            return typeof converted === "object"
              ? converted.primaryQty
              : converted;
          })()
        : enteredQty;
    // console.log("Final Qty to send to server:", finalQty);
    // console.log("secondary selected?", isSecondary);
    setBtnLoading(index);
    try {
      if (Number(finalQty) === Number(item.quantity)) {
        const response = itemData?.unitConfig
          ? await onAccept(transferid, item._id, Number(finalQty), isSecondary)
          : await onAccept(transferid, item._id, Number(finalQty));
        if (response?.status) {
          const updatedItemsFromServer = response.results?.transferitems || [];
          const updatedItem = updatedItemsFromServer.find(
            (i) => i._id === item._id,
          );
          if (!updatedItem) {
            showAlert("error", "Updated item not found in response.");
            return;
          }
          setTransferData((prev) => {
            const updatedItems = prev.transferitems.map((i) =>
              i._id === item._id ? { ...i, ...updatedItem } : i,
            );

            return {
              ...prev,
              transferitems: updatedItems,
            };
          });
          showAlert(
            "success",
            response?.message || "Transfer accepted successfully.",
          );
          setQuantities((prev) => ({ ...prev, [index]: "" }));
          setErrorIndex(null);
        } else {
          showAlert(
            "error",
            response?.message ||
              "No stock transfer data was found for this store",
          );
        }
      } else {
        showAlert("warning", Messages.ERROR.QUANTITY_MISMATCH);
      }
    } catch (error) {
      showAlert("error", "Something went wrong while accepting transfer.");
    } finally {
      setBtnLoading(null);
    }
  };

  // const handleQuantityChange = (index, value) => {
  //   if (value === "" || /^[1-9][0-9]*$/.test(value)) {
  //     setQuantities((prevQuantities) => ({
  //       ...prevQuantities,
  //       [index]: value === "" ? "" : parseInt(value, 10),
  //     }));
  //     if (value && parseInt(value, 10) > 0) {
  //       setErrorIndex(null);
  //     }
  //   }
  // };

  const handleUnitChange = (index, newUnit) => {
    setSelectedUnits((prev) => ({
      ...prev,
      [index]: newUnit,
    }));

    // reset qty when switching units
    setQuantities((prev) => ({
      ...prev,
      [index]: "",
    }));
  };

  const handleQuantityChange = (index, value) => {
    const item = transferData.transferitems[index];

    const hasUnitConfig =
      item?.unitConfig &&
      item?.unitConfig?.primaryUnitCode &&
      item?.unitConfig?.conversion;

    const primaryUnit = item?.unitConfig?.primaryUnitCode;

    const secondaryUnit = item?.unitConfig?.secondaryUnitCode;

    // default selected unit
    const selectedUnit =
      selectedUnits[index] ||
      (item?.isSecondaryUnit ? secondaryUnit : primaryUnit);

    const isSecondarySelected = hasUnitConfig && selectedUnit === secondaryUnit;

    let regex;

    // no unit config → integer only
    if (!hasUnitConfig) {
      regex = /^\d*$/;
    } else {
      regex = isSecondarySelected
        ? /^\d*\.?\d{0,1}$/ // secondary
        : /^\d*\.?\d{0,3}$/; // primary
    }

    if (!regex.test(value)) return;

    setQuantities((prev) => ({
      ...prev,
      [index]: value,
    }));

    if (value && Number(value) > 0) {
      setErrorIndex(null);
    }
  };

  const handleClose = () => {
    setMainModalOpen(false);
    onClose();
  };
  useEscapeKey(handleClose);
  const handleViewItem = async (item) => {
    try {
      if (!checkNetwork()) return;
      const data = {
        productid: item.productid,
        stockid: item.stockid,
        stocklid: item.stocklinkid,
      };
      const response = await AxiosInstance.post(`view-landingcost`, data);
      if (response.data.results.landingUpdatedBy === isUser.storeId) {
        var landingcost = CurrencyConverter.toCurrency(
          response.data.results.landingcost || 0,
          "INR",
        );
      }
      setLandingCost(landingcost);
      setSelectedItem(item);
      setMainModalOpen(false);
      setViewModalOpen(true);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleCloseViewModal = () => {
    setLandingCost("");
    setLandingCostError(null);
    setViewModalOpen(false);
    setSelectedItem(null);
    setMainModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "landingcost") {
      const regex = /^\d*\.?\d{0,2}$/;
      if (value === "" || regex.test(value)) {
        setLandingCost(value);
        setLandingCostError(null);
      } else {
        setLandingCost("");
        setLandingCostError("Enter a valid number");
      }
    }
  };

  const handleUpdate = async () => {
    if (landingcost && !/^\d*\.?\d{0,2}$/.test(landingcost)) {
      setLandingCostError("Enter a valid number");
      return;
    }
    setUpdateBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        productid: selectedItem.productid,
        stockid: selectedItem.stockid,
        stocklid: selectedItem.stocklinkid,
        landingcost: CurrencyConverter.toSubunit(landingcost || 0, "INR"),
      };
      const response = await AxiosInstance.post(`update-landingcost`, data);
      showAlert("success", Messages.SUCCESS.STOCK_UPDATED);
      handleCloseViewModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setUpdateBtnLoading(false);
    }
  };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-label-warning";
      case "accepted":
        return "bg-label-green";
      default:
        return "bg-label-secondary";
    }
  };

  const getText = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "Pending";
      case "ACCEPTED":
        return "Accepted";
      case "RECEIVED":
        return "Received";
      default:
        return status;
    }
  };

  const displayPriceTotalMrp = transferData.transferitems.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
  
    return total + displayPrices.lineTotalMrp ;
  }, 0);

  const displayPriceTotalDisc = transferData.transferitems.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
    return total + displayPrices.lineTotalDisc;
  }, 0);
  
  const displayPriceTotalSalePrice = transferData.transferitems.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
  
    return total+ displayPrices.lineTotalSalePrice;
  }, 0);

  return (
    <div>
      {mainModalOpen && (
        <>
          <div className="modal-backdrop show"></div>
          <div
            className="modal show"
            id="StockTrasferModal"
            tabIndex="-1"
            aria-modal="true"
            role="dialog"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-dialog-scrollable modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title">STOCK ARRIVED</h6>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="pb-2 border-0">
                     <div className="card-body p-0">
                  <div className="row align-items-center">
                      <div className="col-md-8">
                      {isUser.profileData?.image && (
                        <img
                          src={uRLs.IMAGE_URL + "profile/" + isUser.profileData.image}
                          alt="logo"
                          style={{
                            maxWidth: "120px",
                            maxHeight: "80px",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </div>
                     <div className="col-md-2">
                                          <small className="text-muted d-block">Transfer No</small>
                                          <strong>{transferData.transferno}</strong>
                                        </div>
                    
                                        <div className="col-md-2">
                                          <small className="text-muted d-block">Transfer Date</small>
                                          <strong>
                                          {transferData?.transferdate
                                                ? DateConverter.ISOToDisplayFormat(
                                                    transferData.transferdate
                                                  ).date
                                                : "N/A"}
                                          </strong>
                                        </div>
                  </div>
                   <div className="position-relative text-center my-2">
                  <div className="position-absolute top-50 start-0 end-0 border-top translate-middle-y z-0"></div>
                  <span className="position-relative bg-white px-4 text-secondary fw-bold tracking-widest small z-1">
                    TRANSFER RECEIPT
                  </span>
                </div>
                
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div
                        className="p-2 h-100"
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "10px",
                          background: "#f8f9fa",
                        }}
                      >
                        <h6 className="fw-bold text-primary mb-2 font-14">
                          From
                        </h6>

                        <h6 className="mb-1 font-12" >
                          {transferData.transferfrom?.stype} -{" "}
                          {transferData.transferfrom?.title}
                        </h6>

                        <p className="mb-0 text-muted font-12">
                          {transferData.transferfrom?.email}
                        </p>
                      </div>
                    </div>
                <div className="col-md-1 text-center" style={{ marginTop: "2.5rem"}}>
                  <div className="rounded-circle"
                    style={{
                      width: "50px",
                      height: "50px",
                      margin: "auto",        
                      background: "#e8f0fe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                    className="fadeIn animated bx bx-transfer-alt"
                      style={{
                        fontSize: "20px",
                        color: "#0d6efd",
                      }}
                    />
                  </div>
                </div>
                    <div className="col-md-4">
                      <div
                        className="p-2 h-100"
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "10px",
                          background: "#f8f9fa",
                        }}
                      >
                        <h6 className="fw-bold text-success mb-2 font-14">
                          To
                        </h6>

                        <h6 className="mb-1 font-12">
                          {transferData.transferto?.stype} -{" "}
                          {transferData.transferto?.title}
                        </h6>

                        <p className="mb-0 text-muted font-12">
                          {transferData.transferto?.email}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-3"> <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total Mrp :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                        
                        {formatCurrencyFunWithoutConvert(displayPriceTotalMrp)}
                      </h6>
                    </div>
                  <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total Discount :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                        
                        {formatCurrencyFunWithoutConvert(displayPriceTotalDisc)}
                      </h6>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total salePrice :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                       
                        {formatCurrencyFunWithoutConvert(displayPriceTotalSalePrice)}
                      </h6>
                    </div>
                    <div className="bg-light rounded p-2 text-center">
                      <h6 className="mb-0 fs-18 text-dark">
                        Grand Total :{" "}
                        <span>
                          {formatCurrencyFun(transferData?.grandtotal)}
                        </span>
                      </h6>
                    </div></div>
                  </div>
              </div>
              </div>
                  <div className="row">
                    <div className="col-lg-12 table-responsive rtable">
                      <table className="table align-middle table-sm table-bordered mb-0 rtable">
                        <thead className="bg-light">
                          <tr className="text-center">
                            <th>S.No</th>
                            <th>Title</th>
                            <th>MRP</th>
                            <th>Discount</th>
                            <th>Sale Price</th>
                            <th>Total</th>
                            <th>Quantity</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody className="text-end">
                          {transferData.transferitems.map((item, index)=>{
                            const displayPrices = getDisplayPrices(item);
                          return(
                            <tr key={index}>
                              <td className="text-center">{index + 1}</td>
                              <td className="text-start text-wrap">{item.title}</td>
                              <td>
                                {/* ₹
                                {CurrencyConverter.toCurrency(
                                  item.mrp || 0,
                                  "INR",
                                )} */}
                                {formatCurrencyFunWithoutConvert(displayPrices.mrp)}
                              </td>
                              <td>
                                {/* ₹
                                {CurrencyConverter.toCurrency(
                                  item.discount || 0,
                                  "INR",
                                )} */}
                                {formatCurrencyFunWithoutConvert(displayPrices.discount)}
                              </td>
                              <td>
                                {/* ₹
                                {CurrencyConverter.toCurrency(
                                  item.saleprice || 0,
                                  "INR",
                                )} */}
                                {formatCurrencyFunWithoutConvert(displayPrices.saleprice)}
                              </td>
                              <td>
                                {/* ₹
                                {CurrencyConverter.toCurrency(
                                  item.lineGrandTotal || 0,
                                  "INR",
                                )} */}
                                {formatCurrencyFun(item.lineGrandTotal)}
                              </td>
                              <td className="no-print text-center">
                                {/* {item.status === "ACCEPTED" ? (
                                  <div>
                                    {item.quantity} {""}{" "}
                                    {item?.unitConfig?.primaryUnitCode}
                                  </div>
                                ) : (
                                  <div> - </div>
                                )} */}
                                {item.status === "ACCEPTED" ? (
                                 <div>
                                {item?.unitConfig
                              ? item?.isSecondaryUnit === true
                                ? (() => {
                                    const secondaryData = toSecondaryQuantity(
                                      item.quantity || 0,
                                      item.unitConfig,
                                    );

                                    return `${secondaryData?.secondaryQty || 0} ${
                                      secondaryData?.unit || ""
                                    }`;
                                  })()
                                : `${item.quantity || 0} ${
                                    item?.unitConfig?.primaryUnitCode || ""
                                  }`
                              : item?.quantity || 0}
                              </div> ) : (
                                  <div> - </div>
                                )}
                              </td>
                              <td className="no-print">
                                <span
                                  className={`badge rounded-pill ${getColor(
                                    item.status,
                                  )}`}
                                >
                                  {getText(item.status)}
                                </span>
                              </td>
                              <td className="text-center w-15">
                                {item.status === "PENDING" ? (
                                  <div className="d-flex flex-column align-items-center">
                                    <div
                                      className="d-flex align-items-center justify-content-center gap-2"
                                      style={{ minWidth: "220px" }}
                                    >
                                      {/* Qty + Unit */}
                                      <div
                                        className="input-group flex-nowrap"
                                        style={{ width: "140px" }}
                                      >
                                        <input
                                          className="form-control text-center"
                                          type="text"
                                          value={quantities[index] || ""}
                                          onChange={(e) =>
                                            handleQuantityChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() =>
                                            handleInputFocus(index)
                                          }
                                        />

                                        {item?.unitConfig?.primaryUnitCode && (
                                          <select
                                            className="form-select text-center flex-shrink-0"
                                            style={{
                                              maxWidth: "70px",
                                            }}
                                            value={
                                              selectedUnits[index] ||
                                              (item?.isSecondaryUnit
                                                ? item?.unitConfig
                                                    ?.secondaryUnitCode
                                                : item?.unitConfig
                                                    ?.primaryUnitCode)
                                            }
                                            onChange={(e) =>
                                              handleUnitChange(
                                                index,
                                                e.target.value,
                                              )
                                            }
                                          >
                                            <option
                                              value={
                                                item?.unitConfig
                                                  ?.primaryUnitCode
                                              }
                                            >
                                              {
                                                item?.unitConfig
                                                  ?.primaryUnitCode
                                              }
                                            </option>

                                            {item?.unitConfig
                                              ?.secondaryUnitCode && (
                                              <option
                                                value={
                                                  item?.unitConfig
                                                    ?.secondaryUnitCode
                                                }
                                              >
                                                {
                                                  item?.unitConfig
                                                    ?.secondaryUnitCode
                                                }
                                              </option>
                                            )}
                                          </select>
                                        )}
                                      </div>

                                      {/* Accept Button */}
                                      {isBtnLoading === index ? (
                                        <button
                                          className="btn btn-label-success shadow-none btn-sm"
                                          disabled
                                        >
                                          <CircularProgress
                                            size={20}
                                            className="text-success"
                                          />
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="btn btn-label-success shadow-none btn-sm d-flex align-items-center"
                                          onClick={() =>
                                            handleAccept(
                                              transferData._id,
                                              item,
                                              index,
                                            )
                                          }
                                        >
                                          <CheckCircleIcon iClass={"me-1"}/>
                                          Accept
                                        </button>
                                      )}
                                    </div>

                                    {errorIndex === index && (
                                      <div className="invalid-feedback d-block mt-1 text-center">
                                        Enter a valid quantity
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-label-info shadow-none btn-sm"
                                  >
                                    <ChevronRightIcon/>
                                  </button>
                                )}
                              </td>
                            </tr>
                          )})}
                          {/* <tr>
                            <td colSpan="1"> </td>
                            <td className="bg-light">
                              <h6 className="mb-0">Total</h6>
                            </td>
                            <td className="bg-light">
                              <h6 className="mb-0">
                                ₹
                                {CurrencyConverter.toCurrency(
                                  transferData.totalmrp || 0,
                                  "INR",
                                )}
                              </h6>
                            </td>
                            <td className="bg-light">
                              <h6 className="mb-0">
                                ₹
                                {CurrencyConverter.toCurrency(
                                  transferData.totaldiscount || 0,
                                  "INR",
                                )}
                              </h6>
                            </td>
                            <td className="bg-light">
                              <h6 className="mb-0">
                                ₹
                                {CurrencyConverter.toCurrency(
                                  transferData.totalsaleprice || 0,
                                  "INR",
                                )}
                              </h6>
                            </td>
                            <td className="bg-light">
                              <h6 className="mb-0">
                                ₹
                                {CurrencyConverter.toCurrency(
                                  transferData.grandtotal || 0,
                                  "INR",
                                )}
                              </h6>
                            </td>
                            <td></td>
                          </tr> */}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="modal-footer d-block">
                  <button
                    onClick={handleClose}
                    type="button"
                    data-bs-dismiss="modal"
                    className="btn btn-outline-secondary btn-sm text-left"
                  >
                    <CloseIcon iClass={"fs-15"}/> Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {viewModalOpen && selectedItem && (
        <>
          <div className="modal-backdrop show"></div>
          <div
            className="modal show"
            id="ViewItemModal"
            tabIndex="-1"
            aria-modal="true"
            role="dialog"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title">Update Info</h6>
                  <button
                    type="button"
                    onClick={handleCloseViewModal}
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="d-flex align-items-center">
                        <label htmlFor="landingcost" className="form-label">
                          Landing Cost <small>(Per Unit) (Optional)</small>
                        </label>
                        <span className="tooltip-container">
                          <span className="tooltip-icon">?</span>
                          <span className="tooltip-text">
                            Total cost to bring the product to your inventory,
                            including purchase price and taxes.
                          </span>
                        </span>
                      </div>
                      <div className="input-container position-relative">
                        <input
                          type="text"
                          name="landingcost"
                          autoComplete="off"
                          ref={LandingCostRef}
                          className={`form-control text-center`}
                          onFocus={(e) => e.target.select()}
                          onChange={handleChange}
                          value={landingcost}
                        />
                        {landingCostError && (
                          <div className="invalid-feedback">
                            {landingCostError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer d-block">
                  <button
                    type="button"
                    className="btn btn-outline-secondary text-left"
                    onClick={handleCloseViewModal}
                  >
                    <XIcon/> Close
                  </button>
                  {isUpdateBtnLoading ? (
                    <button
                      className="btn btn-success text-right pb-0"
                      disabled
                    >
                      <CircularProgress size={22} className="text-white" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-success text-right"
                      onClick={handleUpdate}
                    >
                      <CloseIcon iClass={"fs-15"}/> Update
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ViewStockArrival;
